import React, { Fragment, useRef, useState, useCallback, useMemo, useEffect, useSyncExternalStore } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useParams } from 'react-router-dom';
import { assetsChaptersBulkUpdateCreate } from '@goldcast/api/content';
import { Tooltip } from 'react-tooltip';
import { getAllSegments, isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { ChapterStartAndEndTime, IndividualChapterPropInterface } from './ChapterItem';
import RenameChapter from './RenameChapter';
import {
  getPlayerTimeFromSegmentTime,
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime,
  MIN_VIDEO_SEGMENT_DURATION
} from '../timeUtils';
import { getCompositeSegment } from '../playerConfigUtil';
import { zoomLevelStore } from '../store/zoomLevel';
import { menuItems } from '@/Pages/Sessions/AssetsTable/AssetsTableUtils';
import { SESSION_SIDEBAR_WIDTH_PX } from '@/Pages/SessionDetail/constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { addPendingUpdate, PendingEditType } from '@/stores/pendingEditsStore';
import { appendChapterHistory } from '@/stores/clipHistory';
import { hasCreatableCompositeSegmentBetweenSegmentIds } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/createClipValidation';
import Icon from '@/components/atoms/Icon';

type MenuItemsLabelType = string;

interface RowMenuItemProps {
  chapter: IndividualChapterPropInterface;
  chapterStartAndEndTime: ChapterStartAndEndTime;
  isDragging: boolean;
}

const getCreateClipValidationState = ({
  isSplitsEnabled,
  clipData,
  chapterStartAndEndTime
}: {
  isSplitsEnabled: boolean;
  clipData: ReturnType<typeof useClipsContext>['clipData'];
  chapterStartAndEndTime: ChapterStartAndEndTime;
}) => {
  const allSegments = getAllSegments(clipData);

  if (isSplitsEnabled) {
    const startSegment = getSegmentAtCurrentFrame(chapterStartAndEndTime.start_time, clipData);
    const endSegment = getSegmentAtCurrentFrame(chapterStartAndEndTime.end_time, clipData);

    if (!startSegment?.id || !endSegment?.id) {
      return { isCreateClipDisabled: true, reason: 'Cannot create clips from inserted video' };
    }

    const endSegmentTime = getSegmentTimeFromPlayerTime(chapterStartAndEndTime.end_time, clipData, endSegment.id);
    const hasCompositeSegment = hasCreatableCompositeSegmentBetweenSegmentIds({
      allSegments,
      startSegmentId: startSegment.id,
      endSegmentId: endSegment.id,
      endSegmentTime,
      minVideoSegmentDuration: MIN_VIDEO_SEGMENT_DURATION
    });

    if (!hasCompositeSegment) {
      return { isCreateClipDisabled: true, reason: 'Cannot create clips from inserted video' };
    }

    return { isCreateClipDisabled: false, reason: '' };
  }

  const compositeSegment = getCompositeSegment(clipData);
  if (!compositeSegment) {
    return { isCreateClipDisabled: true, reason: 'No composite segment available' };
  }
  const clipDuration =
    getPlayerTimeFromSegmentTime(chapterStartAndEndTime.end_time, clipData, compositeSegment.id) -
    getPlayerTimeFromSegmentTime(chapterStartAndEndTime.start_time, clipData, compositeSegment.id);
  if (clipDuration <= 0) {
    return { isCreateClipDisabled: true, reason: 'Playable duration should be greater than 0' };
  }

  return { isCreateClipDisabled: false, reason: '' };
};

export default function ChapterActionMenu({ chapter, chapterStartAndEndTime, isDragging }: RowMenuItemProps) {
  const chapterMenuItem = menuItems['CHAPTER'];
  const { broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isRenamingChapter, setIsRenamingChapter] = useState(false);
  const { clipData } = useClipsContext();
  const { onCreateClip: createClip } = useCreateClip({ clip: clipData });
  const { logger } = useAppContext();
  const [isDeletingChapter, setIsDeletingChapter] = useState(false);
  const chapterId = chapter.id;
  const transcriptStore = useTranscriptContext();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const {
    isOpen: isRemoveChapterConfirmationOpen,
    openDialog: openRemoveChapterConfirmationDialog,
    closeDialog: closeRemoveChapterConfirmationDialog
  } = useDialog();
  const chapterContainerRef = useRef<HTMLDivElement>(null);
  const closeMenuRef = useRef<(() => void) | null>(null);
  const hasMountedRef = useRef(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const { zoomLevel } = useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setIsTooltipOpen(false);
    closeMenuRef.current?.();
  }, [zoomLevel]);

  const isSplitsEnabled = useMemo(
    () =>
      isSplitsInTimelineEnabledPure({
        clip: clipData,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }),
    [clipData]
  );

  const onCreateClip = useCallback(() => {
    let createChapterObj = {
      start_time: chapterStartAndEndTime.start_time,
      end_time: chapterStartAndEndTime.end_time,
      start_segment_id: '',
      end_segment_id: ''
    };
    if (isSplitsEnabled) {
      const startSegment = getSegmentAtCurrentFrame(chapterStartAndEndTime.start_time, clipData);
      const endSegment = getSegmentAtCurrentFrame(chapterStartAndEndTime.end_time, clipData);
      if (startSegment && endSegment) {
        createChapterObj = {
          start_time: getSegmentTimeFromPlayerTime(chapterStartAndEndTime.start_time, clipData, startSegment?.id),
          end_time: getSegmentTimeFromPlayerTime(chapterStartAndEndTime.end_time, clipData, endSegment?.id),
          start_segment_id: startSegment?.id || '',
          end_segment_id: endSegment?.id || ''
        };
      }
    }

    createClip(
      createChapterObj.start_time,
      createChapterObj.end_time,
      createChapterObj.start_segment_id,
      createChapterObj.end_segment_id
    )
      .then(() => {
        showSuccessToast('Clip created successfully');
      })
      .catch(err => {
        showErrorToast(err?.message || 'Error while creating clip');
        logger.error('Error while creating clip', err);
      });
  }, [
    clipData,
    chapterStartAndEndTime.start_time,
    chapterStartAndEndTime.end_time,
    createClip,
    isSplitsEnabled,
    logger
  ]);

  const onRemoveChapter = useCallback(() => {
    if (!chapterId || !broadcastId || !chapters) {
      return;
    }
    setIsDeletingChapter(true);
    const previousChapters = chapters.map(c => ({ ...c }));
    const updatedChapters = previousChapters.map(c => ({ ...c }));
    const chapterIndex = updatedChapters.findIndex(c => c.id === chapterId);
    const toRemoveChapter = updatedChapters[chapterIndex];

    if (chapterIndex === -1 || !toRemoveChapter) {
      setIsDeletingChapter(false);
      closeRemoveChapterConfirmationDialog();
      return;
    }

    if (chapterIndex !== chapters.length - 1) {
      updatedChapters[chapterIndex + 1].start_time = toRemoveChapter.start_time;
    }

    const finalChapters = updatedChapters.filter(c => c.id !== chapterId);
    EventBus.dispatch(CustomEvents.UpdateChapters, {
      chapterData: finalChapters,
      chaptersLastUpdatedAt: new Date().toISOString()
    });
    appendChapterHistory(clipData.id, finalChapters);

    const chapterDeletePromise = assetsChaptersBulkUpdateCreate({
      body: finalChapters,
      queryParams: {
        content_id: broadcastId
      }
    });

    addPendingUpdate({
      clipId: clipData.id,
      updatePromise: chapterDeletePromise,
      version: Date.now(),
      editType: PendingEditType.chapter
    });

    void chapterDeletePromise
      .then(() => {
        EventBus.dispatch(CustomEvents.UpdateChapters, {
          chapterData: finalChapters,
          chaptersLastUpdatedAt: new Date().toISOString()
        });
        showSuccessToast('Chapter removed successfully');
      })
      .catch(err => {
        EventBus.dispatch(CustomEvents.UpdateChapters, {
          chapterData: previousChapters,
          chaptersLastUpdatedAt: new Date().toISOString()
        });
        showErrorToast('Error while removing chapter');
        logger.error('Error while removing chapter', err);
      })
      .finally(() => {
        setIsDeletingChapter(false);
        closeRemoveChapterConfirmationDialog();
      });
  }, [chapterId, broadcastId, chapters, logger, closeRemoveChapterConfirmationDialog, clipData.id]);

  const onAction = useCallback(
    (actionType: MenuItemsLabelType) => {
      switch (actionType) {
        case 'Rename Chapter':
          setIsRenamingChapter(true);
          break;
        case 'Create Clip':
          onCreateClip();
          break;
        case 'Remove Chapter':
          openRemoveChapterConfirmationDialog();
          break;
      }
    },
    [onCreateClip, openRemoveChapterConfirmationDialog]
  );

  const handleMenuButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setClickPosition({ x: e.clientX, y: e.clientY });
    if (!chapterContainerRef.current) {
      return;
    }
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: e.clientX,
      clientY: e.clientY
    });
    chapterContainerRef.current.dispatchEvent(clickEvent);
  }, []);

  useEffect(() => {
    const buttonElement = buttonRef.current;

    if (buttonElement) {
      const handleMouseEnter = () => {
        setIsTooltipOpen(true);
      };
      const handleMouseLeave = () => {
        setIsTooltipOpen(false);
      };

      buttonElement.addEventListener('mouseenter', handleMouseEnter);
      buttonElement.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        if (buttonElement) {
          buttonElement.removeEventListener('mouseenter', handleMouseEnter);
          buttonElement.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }
  }, []);

  const { isCreateClipDisabled, reason } = useMemo(() => {
    return getCreateClipValidationState({
      isSplitsEnabled,
      clipData,
      chapterStartAndEndTime
    });
  }, [isSplitsEnabled, clipData, chapterStartAndEndTime]);

  return (
    <>
      <div className="relative flex w-full" ref={chapterContainerRef}>
        <Menu as="div" className="relative flex w-full items-start justify-start">
          {({ close }) => {
            closeMenuRef.current = close;
            return (
              <>
                <Menu.Button
                  ref={buttonRef}
                  className="group/chapter-container text-c2xs relative w-full truncate rounded-full px-[9px] py-[7px] text-start text-2xs font-normal text-slate-600"
                  data-testid="chapter-actions-menu-button"
                  onClick={handleMenuButtonClick}
                >
                  {chapter.title}
                </Menu.Button>
                <Transition
                  as={Fragment}
                  show={isTooltipOpen && !isDragging}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <div
                    className="fixed -bottom-1 z-20 h-[21px] w-fit -translate-y-full rounded-sm border border-zinc-900 bg-zinc-900 px-1.5 py-0.5 text-2xs leading-[15px] text-white"
                    data-testid="chapter-actions-menu-tooltip"
                    style={
                      buttonRef.current
                        ? {
                            top: `${buttonRef.current.getBoundingClientRect().top - 4}px`,
                            left: `${Math.max(SESSION_SIDEBAR_WIDTH_PX, buttonRef.current.getBoundingClientRect().left)}px`
                          }
                        : {}
                    }
                  >
                    {chapter.title}
                  </div>
                </Transition>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <div
                    className="fixed inset-0 z-[70]"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseMove={e => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Menu.Items
                      style={{
                        position: 'fixed',
                        top: `${clickPosition?.y ?? buttonRef.current?.getBoundingClientRect().top ?? 0}px`,
                        left: `${Math.max(
                          SESSION_SIDEBAR_WIDTH_PX,
                          Math.min(
                            clickPosition?.x ?? buttonRef.current?.getBoundingClientRect().left ?? 0,
                            window.innerWidth - 200
                          )
                        )}px`
                      }}
                      className={
                        'z-50 w-48 -translate-x-1 -translate-y-full rounded-lg border bg-white p-1 text-sm text-black shadow-lg'
                      }
                    >
                      {chapterMenuItem?.map(item => {
                        const isDisabled = item.label === 'Create Clip' && isCreateClipDisabled;
                        return (
                          <Menu.Item as="div" key={item.label}>
                            <button
                              data-tooltip-id={`chapter-menu-${item.label}`}
                              className={
                                'flex w-full items-center space-x-3 rounded-md px-3 py-2 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50'
                              }
                              onClick={() => onAction(item.label as MenuItemsLabelType)}
                              disabled={isDisabled}
                            >
                              <Icon name={item.iconName} size={18} stroke={1.5} />
                              <span>{item.label}</span>
                            </button>
                            {isDisabled && (
                              <Tooltip
                                id={`chapter-menu-${item.label}`}
                                noArrow
                                className="bg-black px-1.5 py-1 text-2xs leading-tight text-white"
                              >
                                {reason}
                              </Tooltip>
                            )}
                          </Menu.Item>
                        );
                      })}
                    </Menu.Items>
                  </div>
                </Transition>
              </>
            );
          }}
        </Menu>
      </div>
      <ConfirmationDialog
        isOpen={isRemoveChapterConfirmationOpen}
        onClose={closeRemoveChapterConfirmationDialog}
        onConfirm={onRemoveChapter}
        confirmLoading={isDeletingChapter}
        title="Remove Chapter"
        content="Are you sure you want to remove this chapter break in the video?"
        confirmLabel="Yes"
        cancelLabel="Cancel"
      />
      {isRenamingChapter && <RenameChapter chapter={chapter} onClose={() => setIsRenamingChapter(false)} />}
    </>
  );
}
